import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DataDeletion from './pages/DataDeletion';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import BrandManagement from './pages/BrandManagement';
import CreativeGeneration from './pages/CreativeGeneration';
import ConversationalCreative from './pages/ConversationalCreativeV2';
import SavedChats from './pages/SavedChats';
import ContentScheduler from './pages/ContentScheduler';
import AssetLibrary from './pages/AssetLibrary';
import Analytics from './pages/Analytics';
import AIInsights from './pages/AIInsights';
import CampaignMonitoring from './pages/CampaignMonitoring';
import AdCreativeAnalyzer from './pages/AdCreativeAnalyzer';
import CampaignCreatives from './pages/CampaignCreatives';
import PlacementInsights from './pages/PlacementInsights';
import CommandCenter from './pages/CommandCenter';
import AdvisorDeepDive from './pages/AdvisorDeepDive';
import CampaignBuilder from './pages/CampaignBuilder';
import Automation from './pages/Automation';
import TeamManagement from './pages/TeamManagement';
import Billing from './pages/Billing';
import UsageHistory from './pages/UsageHistory';
import Notifications from './pages/Notifications';
import AdminConversationFlow from './pages/AdminConversationFlow';
import ShopifyCRO from './pages/ShopifyCRO';
import FunnelBuilder from './pages/FunnelBuilder';
import SavedFunnels from './pages/SavedFunnels';
import StrategyDashboard from './pages/StrategyDashboard';
import AccountSettings from './pages/AccountSettings';
import ProductPhotoStudio from './pages/ProductPhotoStudio';
import { useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';

const publicRoutes = ['/', '/pricing', '/login', '/privacy-policy', '/terms-of-service', '/data-deletion'];

const AppContent = () => {
    const { currentUser } = useAuth();
    const location = useLocation();
    const isPublicRoute = publicRoutes.includes(location.pathname);

    const showNavigation = currentUser && !isPublicRoute;

    return (
        <div style={{ display: 'flex', minHeight: '100vh' }}>
            {showNavigation && <Navigation />}

            <main
                style={{
                    flex: 1,
                    marginLeft: showNavigation ? '260px' : '0',
                    padding: showNavigation ? 'var(--space-8)' : '0',
                    background: 'var(--color-bg-primary)',
                    minHeight: '100vh',
                    transition: 'margin-left var(--transition-base)',
                }}
            >
                <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/data-deletion" element={<DataDeletion />} />

                    {/* Protected routes */}
                    <Route path="/dashboard" element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/strategy" element={
                        <ProtectedRoute>
                            <StrategyDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/brands" element={
                        <ProtectedRoute>
                            <BrandManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/generate" element={
                        <ProtectedRoute>
                            <CreativeGeneration />
                        </ProtectedRoute>
                    } />
                    <Route path="/photo-studio" element={
                        <ProtectedRoute>
                            <ProductPhotoStudio />
                        </ProtectedRoute>
                    } />
                    <Route path="/creative-chat" element={
                        <ProtectedRoute>
                            <SavedChats />
                        </ProtectedRoute>
                    } />
                    <Route path="/conversational" element={
                        <ProtectedRoute>
                            <ConversationalCreative />
                        </ProtectedRoute>
                    } />
                    <Route path="/schedule" element={
                        <ProtectedRoute>
                            <ContentScheduler />
                        </ProtectedRoute>
                    } />
                    <Route path="/library" element={
                        <ProtectedRoute>
                            <AssetLibrary />
                        </ProtectedRoute>
                    } />
                    <Route path="/analytics" element={
                        <ProtectedRoute>
                            <Analytics />
                        </ProtectedRoute>
                    } />
                    <Route path="/insights" element={
                        <ProtectedRoute>
                            <AIInsights />
                        </ProtectedRoute>
                    } />
                    <Route path="/command-center" element={
                        <ProtectedRoute>
                            <CommandCenter />
                        </ProtectedRoute>
                    } />
                    <Route path="/command-center/advisor" element={
                        <ProtectedRoute>
                            <AdvisorDeepDive />
                        </ProtectedRoute>
                    } />
                    <Route path="/campaigns" element={
                        <ProtectedRoute>
                            <CampaignMonitoring />
                        </ProtectedRoute>
                    } />
                    <Route path="/creative-analyzer" element={
                        <ProtectedRoute>
                            <AdCreativeAnalyzer />
                        </ProtectedRoute>
                    } />
                    <Route path="/campaign-creatives" element={
                        <ProtectedRoute>
                            <CampaignCreatives />
                        </ProtectedRoute>
                    } />
                    <Route path="/placement-insights" element={
                        <ProtectedRoute>
                            <PlacementInsights />
                        </ProtectedRoute>
                    } />
                    <Route path="/store-cro" element={
                        <ProtectedRoute>
                            <ShopifyCRO />
                        </ProtectedRoute>
                    } />
                    <Route path="/campaign-builder" element={
                        <ProtectedRoute>
                            <CampaignBuilder />
                        </ProtectedRoute>
                    } />
                    <Route path="/funnel-builder" element={
                        <ProtectedRoute>
                            <FunnelBuilder />
                        </ProtectedRoute>
                    } />
                    <Route path="/saved-funnels" element={
                        <ProtectedRoute>
                            <SavedFunnels />
                        </ProtectedRoute>
                    } />
                    <Route path="/automation" element={
                        <ProtectedRoute>
                            <Automation />
                        </ProtectedRoute>
                    } />
                    <Route path="/team" element={
                        <ProtectedRoute>
                            <TeamManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/billing" element={
                        <ProtectedRoute>
                            <Billing />
                        </ProtectedRoute>
                    } />
                    <Route path="/usage-history" element={
                        <ProtectedRoute>
                            <UsageHistory />
                        </ProtectedRoute>
                    } />
                    <Route path="/notifications" element={
                        <ProtectedRoute>
                            <Notifications />
                        </ProtectedRoute>
                    } />
                    <Route path="/admin/flow" element={
                        <ProtectedRoute>
                            <AdminConversationFlow />
                        </ProtectedRoute>
                    } />
                    <Route path="/account" element={
                        <ProtectedRoute>
                            <AccountSettings />
                        </ProtectedRoute>
                    } />
                </Routes>
            </main>

            <style>{`
        @media (max-width: 768px) {
          main {
            margin-left: 0 !important;
            padding: ${showNavigation ? 'var(--space-4)' : '0'} !important;
          }
        }
      `}</style>
        </div>
    );
};

const App = () => {
    return (
        <NotificationProvider>
            <AppContent />
        </NotificationProvider>
    );
};

export default App;
